'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { OpenStatusBadge } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Hero del tema "vibrant": foto a sangre como protagonista, contenido anclado
 * abajo-izquierda con jerarquía fuerte (kicker → título XL → tagline → chips →
 * CTA con glow + secundario). Distinto del hero classic (centrado, neutral).
 * Lee tokens del tema (--ms-accent, --ms-hero-overlay, --ms-font-display).
 */
export function HeroVibrant({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')

  return (
    <section className="relative w-full overflow-hidden bg-black" style={{ minHeight: 'min(620px, 82vh)' }}>
      {heroVideoUrl ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src={heroVideoUrl}
        />
      ) : heroImageUrl ? (
        <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
      ) : null}

      {/* Overlay diagonal cálido (token) + refuerzo inferior para legibilidad del texto */}
      <div className="absolute inset-0 z-10" style={{ background: 'var(--ms-hero-overlay)' }} />
      <div
        className="absolute inset-0 z-10"
        style={{ background: 'linear-gradient(to top, rgba(20,4,0,0.85) 0%, transparent 55%)' }}
      />

      {/* Contenido abajo-izquierda */}
      <div
        className="absolute inset-x-0 bottom-0 z-20 px-6 sm:px-10 pb-11"
        style={{ maxWidth: '1120px' }}
      >
        {city && (
          <span className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.22em] text-white/85 mb-4">
            <span
              className="w-[6px] h-[6px] rounded-full"
              style={{ background: 'var(--ms-accent)', boxShadow: '0 0 0 4px color-mix(in srgb, var(--ms-accent) 25%, transparent)' }}
            />
            {city}
          </span>
        )}

        <h1
          className="text-white font-black tracking-[-0.02em] text-[clamp(46px,9vw,84px)] leading-[0.9]"
          style={{ fontFamily: 'var(--ms-font-display)', textShadow: '0 4px 24px rgba(0,0,0,0.4)' }}
        >
          {businessName}
        </h1>

        {tagline && <p className="text-base sm:text-lg text-white/85 mt-3 max-w-xl">{tagline}</p>}

        {/* Chips */}
        <div className="flex flex-wrap items-center gap-2 mt-5">
          <OpenStatusBadge status={openStatus ?? null} />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/14 backdrop-blur-sm border border-white/20 text-white px-3 py-1.5 text-[13px] font-medium">
            {t('pickupAvailable')}
          </span>
          {pickupWaitMinutes ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/14 backdrop-blur-sm border border-white/20 text-white px-3 py-1.5 text-[13px] font-medium">
              {readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}
            </span>
          ) : null}
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap items-center gap-3 mt-7">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-[17px] font-extrabold text-white transition hover:scale-[1.03] active:scale-95"
            style={{ background: 'var(--ms-accent)', boxShadow: '0 14px 32px -8px var(--ms-accent)' }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center rounded-full px-6 py-4 text-[15px] font-bold text-white border border-white/40 transition hover:bg-white/10"
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
